import { ConnectionType } from 'src/app/+shared/enums/connection-types.enum';
import {
  ContactPerson,
  Document,
  UpdateBusinessPartnerData,
  UpdateConnectionObjectData,
  UpdateInvoiceAddressData,
} from '../jurisdiction.domain';

export interface FormSubsectionsVisibility {
  gas: boolean;
  heating: boolean;
  power: boolean;
  water: boolean;
}

export interface GridConnectionIds {
  businessPartnerId: string;
  connectionObjectId: string;
  gasConnectionId: string;
  heatingConnectionId: string;
  powerConnectionId: string;
  waterConnectionId: string;
}

export interface CreateGridConnectionResponse {
  gridconapplication: {
    application_id: string;
  }[];
}

export interface GetGridConnectionObject {
  gridconapplication: GridConnectionData[];
}

export interface GridConnectionData {
  sws_feedin_available?: string;
  sws_wallbox_available?: string;
  sws_interest_in_fiberopt?: string;
  sk_swkn_required_fuse_power?: string;
  sk_swkn_shutdown_reason?: string;
  swkn_shutdown_reason_details?: string;
  swkn_wideband_con?: string;
  swkn_telecom_con?: string;
  application_id?: string;
  application_ext_id?: string;
  application_date?: string;
  applicant_is_landowner?: string;
  applicant_is_invoicerecipient?: string;
  building_con_from?: string;
  comment?: string;
  desired_date?: string;
  sk_status?: string;
  sk_gridconapplication_type?: string;
  sk_change_subtype?: string;
  sk_building_type?: string;
  user_id?: string;
  over_30_meter?: string;
  has_residential_unit: string;
  has_commercial_unit: string;
  residential_unit_count: string;
  commercial_unit_count: string;
  public_facility: string;
  determinated_price?: string;
  development_area?: string;
  earthwork_own_contribution: string;
  generation_unit_included: string;
  planned_gen_larger_30: string;
  site_power_required: string;
  site_water_required: string;
  businesspartner?: UpdateBusinessPartnerData;
  connectionobject?: UpdateConnectionObjectData;
  powergridcon?: PowerGridConnectionData;
  gasgridcon?: GasGridConnectionData;
  watergridcon?: WaterGridConnectionData;
  districtheatgridcon?: HeatingGridConnectionData;
  invoicerecipient?: UpdateInvoiceAddressData;
  document?: Document[];
  technician_id?: string;
  change_power?: string;
  change_gas?: string;
  change_water?: string;
  change_heat?: string;
  sws_trench_length_total?: string;
  sws_trench_length_unpaved?: string;
  sws_with_basement?: string;
  sws_con_lead_basement?: string;
  sws_waterproof_basement?: string;
  sws_con_lead_main_building?: string;
  sws_third_party_affected?: string;
  sws_plot_size?: string;
  ordnance_area?: string;
  ffb_ha_length_private_prop?: string;
  ffb_basement_available?: string;
  ffb_serviceroom_available?: string;
  reason_for_reinforcement?: string;
  connection_type?: ConnectionType;
  sk_power_connection_type?: string;
  contact_person?: ContactPerson;
  notify_on_submit?: boolean;
}

export interface CreateGridConnectionRequest {
  sk_building_type?: string;
  sk_change_subtype?: string;
  development_area?: string;
  sk_gridconapplication_type: string;
  change_power?: string;
  change_gas?: string;
  change_water?: string;
  change_heat?: string;
  ordnance_area?: string;
  automatic_offer?: string;
  connectionobject?: UpdateConnectionObjectData;
  sk_power_connection_type?: string;
  notify_on_submit?: boolean;
  applicant_is_invoicerecipient?: string;
  applicant_is_landowner?: string;
}

//#region "data models for Gas Grid Connection forms"
export interface CreateGasGridConnectionResponse {
  gasgridcon: {
    gridcon_id: string;
  }[];
}

export interface GasGridConnectionData {
  gridcon_id?: string;
  application_id?: string;
  required_total_power?: string;
  heating_gas?: string;
  kitchen_gas?: string;
  bhkw?: string;
  process_gas?: string;
  required_pressure?: string;
  resp_technician_ident?: string;
  resp_technician_company?: string;
  resp_technician_phone?: string;
  disposal_of_oil_tank?: string;
  size_of_tank?: string;
  boiler_output_heating_gas?: string;
  water_treatment?: string;
  water_protection_area?: string;
}

export interface UpdateGasGridConnection {
  gasgridcon: GasGridConnectionData[];
}

//#endregion "data models for Gas Grid Connection forms"

//#region "data models for Heating Grid Connection forms"
export interface CreateHeatingGridConnectionResponse {
  districtheatgridcon: {
    gridcon_id: string;
  }[];
}

export interface HeatingGridConnectionData {
  gridcon_id?: string;
  application_id?: string;
  heatingload?: string;
  peakflow?: string;
  sk_districtheat_distribution_type?: string;
  sk_districtheat_heating_type?: string;
  drinking_water?: string;
  compactstation?: string;
  individual_billing?: string;
  sk_hotwater_prep_type?: string;
  smoke_alarm_service?: string;
  sk_variable_costs?: string;
  resp_technician_ident?: string;
  resp_technician_company?: string;
  resp_technician_phone?: string;
}

export interface UpdateHeatingGridConnection {
  districtheatgridcon: HeatingGridConnectionData[];
}

//#endregion "data models for Heating Grid Connection forms"

//#region "data models for Power Grid Connection forms"
export interface CreatePowerGridConnectionResponse {
  powergridcon: {
    gridcon_id: string;
  }[];
}

export interface PowerGridConnectionData {
  sk_swkn_required_fuse_power?: string;
  gridcon_id?: string;
  application_id?: string;
  required_total_power?: string;
  specialconsumer?: PowerSpecialConsumerData[];
  resp_technician_ident?: string;
  resp_technician_company?: string;
  resp_technician_phone?: string;
  crane_available?: boolean;
  pump_available?: boolean;
  installation_location_type?: string | null;
  installation_location_type_other?: string | null;
  оffer_earthworks_on_private_property?: boolean | null;
  power_connection_heating_type?: string | null;
  power_connection_heating_type_other?: string | null;
  is_temp_connection_less30_required?: boolean | null;
  technician_bdew_id?: string | null;
  earthwork_area_name?: string | null;
  other_commodities_for_installation_gas?: boolean | null;
  other_commodities_for_installation_heating?: boolean | null;
  other_commodities_for_installation_water?: boolean | null;
  other_commodities_for_installation_other?: boolean | null;
  other_commodities_for_installation_other_input?: string | null;
  temp_power_report_confirmed?: boolean;
}

export interface UpdatePowerGridConnection {
  powergridcon: PowerGridConnectionData[];
}

export interface CreatePowerSpecialConsumerResponse {
  specialconsumer: {
    specialconsumer_id: string;
  }[];
}

export interface PowerSpecialConsumerData {
  specialconsumer_id?: string;
  gridcon_id?: string;
  sk_specialconsumer_type?: string;
  sk_tmp_specialconsumer_type?: string;
  consumer_count: string;
  power: string;
  description: string;
  other_type_details?: string;
  module_type?: string;
  is_controllable_facility?: string;
  is_public_facility?: string;
  zerez_id?: string;
  ski_key?: string;
  date_of_commissioning?: string;
  consumption_device_control?: string;
}

export interface UpdatePowerSpecialConsumer {
  powergridconspecialconsumer: PowerSpecialConsumerData[];
}

//#endregion "data models for Power Grid Connection forms"

//#region "data models for Water Grid Connection forms"
export interface CreateWaterGridConnectionResponse {
  watergridcon: {
    gridcon_id: string;
  }[];
}

export interface WaterGridConnectionData {
  gridcon_id?: string;
  application_id?: string;
  peakflow?: string;
  specialservice?: string;
  specialservice_desc?: string;
  firewater?: string;
  firewater_type?: string;
  individual_billing?: string;
  sk_hotwater_prep_type?: string;
  resp_technician_ident?: string;
  resp_technician_company?: string;
  resp_technician_phone?: string;
  swkn_parcel_size?: string;
  swkn_firewater_peakflow?: string;
}

export interface UpdateWaterGridConnection {
  watergridcon: WaterGridConnectionData[];
}

export interface UpdateChangeHouseConnectionMedien {
  sk_gridconapplication_type: string;
  sk_change_subtype: string;
  change_power: string;
  change_gas: string;
  change_water: string;
  change_heat: string;
}

//#endregion "data models for Water Grid Connection forms"

export type PowerConnectionType = 'standard_mains_connection' | 'special_mains_connection';

export const SPECIAL_CONSUMER_DOCUMENT_TYPES = ['pv_device_consumer', 'bhkw_kwk_device_consumer', 'storage_consumer'];

export interface SpecialConsumerDeviceTypeChange {
  consumerId: string | null;
  tempKey?: string | null;
  previousType: string | null;
  newType: string | null;
}

export interface SpecialConsumerIdentity {
  tempKey: string;
  consumerId: string | null;
}
